import moment from 'moment-timezone-custom';

import { useRouter } from 'next/router';
import qs from 'qs';
import React from 'react';
import LiscensedAssetsTable from '@/components/Table/LiscensedAssetsTable';
import ErrorComponent from '@/components/common/ErrorComponent';
import SeoHead from '@/components/head/SeoHead';
import { seoDescriptionDeal } from '@/components/head/seoDescriptionDeal';
import LoadingView from '@/components/layout/LoadingView';
import PageContainer from '@/components/layout/PageContainer';
import { DealComponent } from '@/components/sections/DealComponent';
import { DATE_FORMATS } from '@/constants/constants';
import { usePageEntrance } from '@/hooks/usePageEntrance';
import type { DealFragment } from '@/utilities/__generated__/graphql';
import { PaymentCurrency_Enum } from '@/utilities/__generated__/graphql';
import { BucketType, createBucketURL } from '@/utilities/createBucketUrl';
import { loadDeal } from '@/utilities/deals/loadDealById';
import { loadDeals } from '@/utilities/deals/loadDeals';
import { cleanAddress } from '@/utilities/functions';
import type { GetStaticProps, NextPage } from 'next';

type Props = {
  deal: DealFragment;
  ogImage: string;
  pagePath: string;
};

const DealDetail: NextPage<Props> = ({
  deal,
  ogImage,
  pagePath,
}: {
  deal: DealFragment;
  ogImage: string;
  pagePath: string;
}) => {
  const router = useRouter();

  const requestedNumberOfNfts = deal?.CollectionDeals.reduce(
    (
      acc: number,
      {
        count,
      }: {
        count: number;
      }
    ) => {
      return acc + count;
    },
    0
  );

  const numberOfCollections = deal?.CollectionDeals.length;

  if (router.isFallback) {
    return <LoadingView />;
  }

  if (!deal) {
    // TODO: figure out why this isn't rerouting but fixing old message
    return (
      <ErrorComponent
        description="Oops! The page you are looking for seems to be missing or may have been moved. We apologize for the inconvenience."
        title="Oops!"
        type="404"
      >
        <p className="max-w-sm text-center text-primary-main">
          If you believe you should have access or need any assistance, please
          reach out to our team on Twitter at{' '}
          <a
            className="text-white"
            href="https://twitter.com/OverpassIP"
            target="_blank"
            rel="noreferrer"
          >
            @OverpassIP.
          </a>
        </p>
      </ErrorComponent>
    );
  }

  const getDate = (date: string) => {
    // create a new moment object from the UTC string
    const dateMoment = moment.utc(date);

    // convert the UTC moment object to the user's local time zone
    const localMoment = dateMoment.tz(moment.tz.guess());

    // format the local moment object as a string in the desired format
    return localMoment.format(DATE_FORMATS.MMMDYhm);
  };

  const proposal_expiration = getDate(deal?.bidding_expiration);
  const license_expiration = getDate(deal?.license_expiration);

  const newDeal = {
    ...deal,
    bidding_expiration: proposal_expiration,
    license_expiration: license_expiration,
  };
  // eslint-disable-next-line react-hooks/rules-of-hooks
  usePageEntrance();
  return (
    <PageContainer classes="space-y-4">
      <div className="rounded-2xl bg-white px-4 py-6 md:px-14 md:py-16">
        {deal?.CollectionDeals.length ? (
          <SeoHead
            imageURI={ogImage}
            pagePath={pagePath}
            title={`OverpassIP | Opportunity Review - ${deal?.Brand.company_name} ${deal?.title}`}
            description={seoDescriptionDeal({
              companyName: deal?.Brand?.company_name ?? '',
              collectionName: deal?.CollectionDeals[0].Collection.name ?? '',
              currency: deal.payment_currency ?? PaymentCurrency_Enum.Usdc,
              minBudget: deal.min_budget,
              maxBudget: deal.max_budget,
              royaltyPercentage: deal.royalty_percentage,
              requestedNumberOfNfts,
              numberOfCollections,
            })}
          />
        ) : null}
        <DealComponent
          deal={deal ? newDeal : deal}
          requestedNumberOfNfts={requestedNumberOfNfts}
        />
        <LiscensedAssetsTable executed_bids={deal?.executed_bids} />
      </div>
    </PageContainer>
  );
};

export const getStaticPaths = async () => {
  const currentTime = moment().tz(moment.tz.guess()).format();
  const { deals } = await loadDeals({ date: currentTime, limit: 15 });

  return {
    paths: deals.map((deal) => `/deal/${deal.id}`),
    fallback: true,
  };
};
export const getStaticProps: GetStaticProps = async (context: any) => {
  const { opportunity } = await loadDeal({
    id: context.params.id,
    isAdmin: true,
  });

  const host =
    process.env.NEXTAUTH_URL ||
    (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : null);

  // Return early if there's no opportunity or host
  if (!opportunity || !host) {
    return {
      props: {},
      revalidate: 60,
    };
  }

  const brand = createBucketURL(BucketType.Brand, opportunity.Brand.username);
  const title = opportunity.Brand.company_name;
  const dealName = opportunity.title;
  const description = opportunity.description;

  const collectionParams = opportunity.CollectionDeals.map((deal) => {
    return deal?.Collection?.nft_contract_address
      ? createBucketURL(
          BucketType.Collection,
          cleanAddress(deal.Collection.nft_contract_address)
        )
      : null;
  }).filter(Boolean);

  const params: {
    [key: string]: string | undefined;
  } = {
    brand,
    title: title || undefined,
    dealName,
    description: description
      ? description.length > 125
        ? description.substring(0, 125) + '...'
        : description
      : undefined,
    collection_count: String(collectionParams.length),
    ...Object.fromEntries(
      collectionParams.map((url, index) => [
        `collection${index + 1}`,
        url || undefined,
      ])
    ),
  };

  const queryString = qs.stringify(params);
  const urlString = `/api/og/deal?${queryString}`;
  const ogImage = host + urlString;

  return {
    props: {
      deal: opportunity,
      ogImage,
      pagePath: `${host}/deal/${context.params.id}`,
    },
    revalidate: 60,
  };
};

export default DealDetail;
